import React, { useState, useEffect, useRef, useMemo } from "react";
import { Menu, Tooltip, Tour } from "antd";
import { MenuFoldOutlined, MenuUnfoldOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import Logo from "./Logo";
import { menuItemsV2 } from "../utils/sideBarUtilsV2";
import { menuTourContent, menuTourIntro } from "../utils/menuTourV2";
import { rightsService } from "../services/rightsService";
import { inlineStyles } from "./ui/sidebarStyles";

const LAYOUT_BREAKPOINT = 1200;
// Un lien externe (http/https absolu) s'ouvre dans un NOUVEL onglet ; les routes internes de la
// SPA restent dans l'onglet courant.
const isExternalLink = (l) => typeof l === "string" && /^https?:\/\//i.test(l);
const externalLinkProps = (l) => (isExternalLink(l) ? { target: "_blank", rel: "noopener noreferrer" } : {});
// Le menu v2 est désormais le SEUL menu : la bascule vers la v1 a été retirée (les écrans
// récents ne sont référencés que dans sideBarUtilsV2, l'ancien menu les rendait invisibles).
// Tour de garde de présentation du menu — joué automatiquement une seule fois.
const MENU_V2_TOUR_KEY = "poolins_menu_v2_tour_done";

const SideBar = ({ currentMenu = "DASHBOARD", backgroundColor = "#212466" }) => {
    const [collapsed, setCollapsed] = useState(false);
    const [openKeys, setOpenKeys] = useState([""]);
    const activeMenuItems = menuItemsV2;

    // ── Tour de garde du menu v2 ────────────────────────────────────────────
    const switchRef = useRef(null);
    const [tourOpen, setTourOpen] = useState(false);

    // Lancement automatique une seule fois (puis rejouable via le bouton « ? »).
    useEffect(() => {
        try {
            if (localStorage.getItem(MENU_V2_TOUR_KEY) !== "1") {
                setTourOpen(true);
                localStorage.setItem(MENU_V2_TOUR_KEY, "1");
            }
        } catch (e) { /* noop */ }
    }, []);

    // Étapes : accueil → switch → un menu par étape (filtré par habilitation, si v2 actif).
    const tourSteps = useMemo(() => {
        const hasMenuRight = (item) => !item.right || rightsService(item.right);

        const getAccessibleChildren = (children) => (children || [])
            .filter(hasMenuRight)
            .map((child) => ({
                ...child,
                children: child.children ? getAccessibleChildren(child.children) : undefined,
            }))
            .filter((child) => !child.children || child.children.length > 0);

        const renderTourItems = (items, content, level = 0) => (
            <ul style={{ margin: level === 0 ? 0 : "4px 0 0", paddingLeft: level === 0 ? 18 : 16 }}>
                {items.map((item) => (
                    <li key={item.key} style={{ marginBottom: 4, fontSize: 13 }}>
                        <strong>{item.label}</strong>{content.items?.[item.key] ? ` — ${content.items[item.key]}` : ""}
                        {item.children?.length > 0 && renderTourItems(item.children, content, level + 1)}
                    </li>
                ))}
            </ul>
        );

        const renderMenuStep = (content, kids) => (
            <div>
                <p style={{ margin: "0 0 8px" }}>{content.utilite}</p>
                {content.nouveaute && (
                    <p style={{ margin: "0 0 8px", padding: "6px 9px", background: "#f0f5ff", border: "1px solid #d6e4ff", borderRadius: 6, fontSize: 13 }}>
                        <strong>Ce qui change :</strong> {content.nouveaute}
                    </p>
                )}
                {kids.length > 0 && (
                    renderTourItems(kids, content)
                )}
            </div>
        );

        const steps = [
            {
                title: menuTourIntro.welcomeTitle,
                description: (
                    <div>
                        <p style={{ margin: 0 }}>{menuTourIntro.welcome}</p>
                        <p style={{ marginTop: 8, color: "#8c8c8c", fontSize: 12 }}>{menuTourIntro.replayHint}</p>
                    </div>
                ),
            },
        ];

        // Une étape par menu (l'étape « bascule v1/v2 » a disparu avec la bascule elle-même).
        menuItemsV2.forEach((group) => {
            const content = menuTourContent[group.key];
            if (!content) return; // liens externes / non documentés
            if (!hasMenuRight(group)) return; // habilitation identique au menu
            const kids = getAccessibleChildren(group.children);
            if (group.children?.length > 0 && kids.length === 0) return;
            steps.push({
                title: group.label,
                description: renderMenuStep(content, kids),
                target: () => document.querySelector(`[data-menu-id$="-${group.key}"]`),
            });
        });
        // Libellés FR des boutons de navigation (AntD affiche Previous/Next/Finish par défaut).
        return steps.map((s, idx) => ({
            ...s,
            prevButtonProps: { children: "Précédent" },
            nextButtonProps: { children: idx === steps.length - 1 ? "Terminer" : "Suivant" },
        }));
    }, []);

    const closeTour = () => setTourOpen(false);

    // Appliquer la classe au body au montage du composant
    useEffect(() => {
        const htmlElement = document.documentElement;
        if (collapsed) {
            htmlElement.classList.add("layout-menu-collapsed");
            htmlElement.classList.remove("layout-menu-expanded");
        } else {
            htmlElement.classList.add("layout-menu-expanded");
            htmlElement.classList.remove("layout-menu-collapsed");
        }
    }, [collapsed]);

    const isSmallScreen = () => {
        return window.innerWidth < LAYOUT_BREAKPOINT;
    };

    const toggleCollapsed = () => {
        setCollapsed(!collapsed);
        if (!isSmallScreen()) {
            const htmlElement = document.documentElement;
            if (!collapsed) {
                htmlElement.classList.remove("layout-menu-expanded");
                htmlElement.classList.add("layout-menu-collapsed");
            } else {
                htmlElement.classList.remove("layout-menu-collapsed");
                htmlElement.classList.add("layout-menu-expanded");
            }
        }
    };

    const onOpenChange = (keys) => {
        setOpenKeys(keys);
    };

    // Convertit récursivement les enfants (supporte les sous-menus IMBRIQUÉS : un enfant peut
    // lui-même avoir des `children`). Un enfant sans droit autorisé est filtré.
    const convertChildItems = (children) => (children || [])
        .map((child) => {
            if (child.right && !rightsService(child.right)) return null;
            if (child.children) {
                return {
                    key: child.key,
                    label: child.label,
                    children: convertChildItems(child.children),
                };
            }
            return {
                key: child.key,
                label: <a href={child.link} style={{ color: "inherit" }} {...externalLinkProps(child.link)}>{child.label}</a>,
            };
        })
        .filter(Boolean);

    const convertToMenuItems = (items) => {
        return items.map((item) => {
            if (item.right && !rightsService(item.right)) {
                return null;
            }
            if (item.type === "divider") {
                return {
                    type: "group",
                    label: !collapsed && (
                        <div style={inlineStyles.sectionHeader(collapsed)}>
                            {item.section}
                        </div>
                    ),
                };
            }

            if (item.children) {
                return {
                    key: item.key,
                    icon: collapsed ? (
                        <Tooltip title={item.label} placement="right">
                            {item.icon}
                        </Tooltip>
                    ) : (
                        item.icon
                    ),
                    label: item.label,
                    children: convertChildItems(item.children),
                    style: inlineStyles.submenuTitleStyle,
                };
            }

            return {
                key: item.key,
                icon: collapsed ? (
                    <Tooltip title={item.label} placement="right">
                        {item.icon}
                    </Tooltip>
                ) : (
                    item.icon
                ),
                label: <a href={item.link} style={{ color: "inherit" }} {...externalLinkProps(item.link)}>{item.label}</a>,
                style: inlineStyles.menuItemStyle,
            };
        }).filter(Boolean);
    };

    return (
        <>
        <Tour open={tourOpen} onClose={closeTour} onFinish={closeTour} steps={tourSteps} />
        <aside style={inlineStyles.sidebar(collapsed, backgroundColor)}>
            {/* Header avec logo et toggle */}
            <div style={inlineStyles.header(collapsed)}>
                {!collapsed && (
                    <div style={{ background: "white", padding: "0px", borderRadius: "8px", maxWidth: "120px", margin: "0 auto", alignItems: "center" }}>
                        <Logo />
                    </div>
                )}
                <div
                    style={inlineStyles.toggleButton}
                    onClick={toggleCollapsed}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.background = "rgba(255, 255, 255, 0.15)";
                        e.currentTarget.style.transform = "scale(1.05)";
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.background = "rgba(255, 255, 255, 0.1)";
                        e.currentTarget.style.transform = "scale(1)";
                    }}
                >
                    {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                </div>
            </div>

            {/* Bouton du tour guidé (la bascule v1/v2 a été retirée : v2 est le seul menu) */}
            {!collapsed && (
                <div ref={switchRef} style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "6px 0 4px" }}>
                    <Tooltip title="Visite guidée du menu" placement="right">
                        <QuestionCircleOutlined
                            onClick={() => setTourOpen(true)}
                            style={{ color: "rgba(255,255,255,0.7)", cursor: "pointer", fontSize: 14, marginLeft: 2 }}
                        />
                    </Tooltip>
                </div>
            )}

            {/* Menu */}
            <div style={inlineStyles.menuContainer}>
                <Menu
                    mode="inline"
                    selectedKeys={[currentMenu]}
                    openKeys={openKeys}
                    onOpenChange={onOpenChange}
                    inlineCollapsed={collapsed}
                    items={convertToMenuItems(activeMenuItems)}
                    style={inlineStyles.menuStyle}
                    theme="dark"
                    inlineIndent={20}
                />
            </div>

            <style>
                {`
                    /* Scrollbar personnalisée */
                    aside::-webkit-scrollbar {
                        width: 6px;
                    }
                    aside::-webkit-scrollbar-track {
                        background: transparent;
                    }
                    aside::-webkit-scrollbar-thumb {
                        background: rgba(255, 255, 255, 0.2);
                        border-radius: 3px;
                    }
                    aside::-webkit-scrollbar-thumb:hover {
                        background: rgba(255, 255, 255, 0.3);
                    }

                    /* Ajustement du layout-page en fonction de l'état de la sidebar */
                    .layout-menu-expanded .layout-page {
                        padding-left: 260px !important;
                        transition: padding-left 0.3s ease;
                    }
                    
                    .layout-menu-collapsed .layout-page {
                        padding-left: 80px !important;
                        transition: padding-left 0.3s ease;
                    }

                    /* Styles du menu Ant Design */
                    .ant-menu-dark {
                        background: transparent !important;
                    }
                    .ant-menu-dark .ant-menu-item {
                        color: rgba(255, 255, 255, 0.85) !important;
                        background: rgba(255, 255, 255, 0.04) !important;
                        margin: 6px 0 !important;
                        border-radius: 10px !important;
                        transition: all 0.3s ease !important;
                    }
                    .ant-menu-dark .ant-menu-item:hover {
                        background: rgba(74, 144, 226, 0.15) !important;
                        color: #ffffff !important;
                        transform: translateX(4px);
                    }
                    .ant-menu-dark .ant-menu-item-selected {
                        background:  rgba(255, 255, 255, 0.04) !important;
                        color: #ffffff !important;
                        box-shadow: 0 4px 12px rgba(74, 144, 226, 0.3) !important;
                    }
                    .ant-menu-dark .ant-menu-submenu-title {
                        color: rgba(255, 255, 255, 0.85) !important;
                        background: rgba(255, 255, 255, 0.14) !important;
                        border-radius: 10px !important;
                        margin: 6px 0 !important;
                        transition: all 0.3s ease !important;
                    }
                    .ant-menu-dark .ant-menu-submenu-title:hover {
                        background: rgba(74, 144, 226, 0.15) !important;
                        color: #ffffff !important;
                    }
                    .ant-menu-dark .ant-menu-submenu-open > .ant-menu-submenu-title {
                        background: rgba(74, 144, 226, 2.9) !important;
                        color: #ffffff !important;
                    }
                    .ant-menu-dark .ant-menu-sub {
                        background: rgba(0, 0, 0, 0.15) !important;
                        border-radius: 8px !important;
                        padding: 4px 0 !important;
                        margin-top: 4px !important;
                    }
                    .ant-menu-dark .ant-menu-item-group-title {
                        padding: 0 !important;
                        color: transparent !important;
                    }
                    .ant-menu-inline-collapsed > .ant-menu-item,
                    .ant-menu-inline-collapsed > .ant-menu-submenu > .ant-menu-submenu-title {
                        padding: 0 calc(50% - 18px / 2) !important;
                    }
                    .ant-menu-inline-collapsed .ant-menu-item {
                        background: rgba(255, 255, 255, 0.05) !important;
                    }
                    .ant-menu-inline-collapsed .ant-menu-item:hover {
                        background: rgba(74, 144, 226, 0.2) !important;
                    }
                    .ant-menu-inline-collapsed .ant-menu-item-selected {
                        background: linear-gradient(135deg, #4a90e2 0%, #357abd 100%) !important;
                    }
                `}
            </style>
        </aside>
        </>
    );
};

export default SideBar;
